import { ApolloLink, CombinedGraphQLErrors } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { isNestJsGraphQLError, logErrorMessages } from '@pcg/graphql-kit';

import { goToLogin } from '@/shared/tools/auth';

/**
 * Correlates a browser request with the API's log line for it. The API reads
 * `x-request-id` and echoes it back in `extensions.context.requestId`.
 */
const generateRequestId = (): string => {
  const random = crypto.randomUUID().replaceAll('-', '').slice(0, 11);

  return `dcreq:${random}`;
};

export const apolloRequestIdLink = setContext((_, { headers }) => ({
  headers: {
    ...headers,
    'x-request-id': generateRequestId(),
  },
}));

/**
 * Puts the operation name in the query string. It changes nothing about the
 * request the server sees (the body is what counts) and everything about
 * reading the network tab, where every row would otherwise say `graphql`.
 */
export const apolloOperationNameLink = new ApolloLink((operation, forward) => {
  operation.setContext(() => ({
    uri: `${import.meta.env.VITE_DC_API_GQL_URL}?operation=${operation.operationName}`,
  }));

  return forward(operation);
});

/**
 * The session cookie is HttpOnly, so JS cannot tell an expired session from a
 * live one — the API is the only source of truth, and this link is where the
 * answer arrives. `goToLogin` carries its own single-flight latch: this
 * handler fires once per failed operation, and a page with several queries in
 * flight would otherwise queue as many redirects.
 */
export const apolloErrorLink = onError((handlerOpts) => {
  const { error } = handlerOpts;

  if (!CombinedGraphQLErrors.is(error)) {
    return;
  }

  const unauthenticated = error.errors.some(
    (err) =>
      isNestJsGraphQLError(err)
      && (err.extensions.statusCode === 401
        || err.extensions.key === 'SESSION_INVALID'
        || err.extensions.key === 'SESSION_EXPIRED'),
  );

  if (unauthenticated) {
    goToLogin();

    return;
  }

  // Once, not once per error: `logErrorMessages` takes the whole handler
  // payload and prints every error in it, so calling it inside the loop dumped
  // the same N errors N times.
  if (error.errors.some(isNestJsGraphQLError)) {
    logErrorMessages(handlerOpts);
  }
});
